"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableRowMemoized = exports.TableRow = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const index_js_1 = require("../utils/index.js");
const TableCell_js_1 = require("./TableCell.js");
/**
 * Memoization is needed to avoid unnecessary re-renders of all rows when additional data is added when lazy-loading.
 * Using `isLast` here instead of passing data length to avoid re-renders of all rows when more data is added. Now only the last row re-renders.
 * Although state is not used it is needed for `React.memo` to check state that changes row state e.g. selection.
 * When adding new features check whether it changes state that affects row. If it does then add equality check to `React.memo`.
 */
const TableRow = (props) => {
    var _a;
    const { row, rowProps, isLast, onRowInViewport, onBottomReached, intersectionMargin, onClick, subComponent, isDisabled, tableHasSubRows, tableInstance, expanderCell, bodyRef, tableRowRef, density, } = props;
    const onIntersect = React.useCallback(() => {
        var _a, _b;
        (_a = onRowInViewport.current) === null || _a === void 0 ? void 0 : _a.call(onRowInViewport, row.original);
        isLast && ((_b = onBottomReached.current) === null || _b === void 0 ? void 0 : _b.call(onBottomReached));
    }, [isLast, onBottomReached, onRowInViewport, row.original]);
    const intersectionRoot = React.useMemo(() => {
        var _a, _b;
        const isTableBodyScrollable = ((_a = bodyRef === null || bodyRef === void 0 ? void 0 : bodyRef.scrollHeight) !== null && _a !== void 0 ? _a : 0) > ((_b = bodyRef === null || bodyRef === void 0 ? void 0 : bodyRef.offsetHeight) !== null && _b !== void 0 ? _b : 0);
        // If table body is scrollable, make it the intersection root
        if (isTableBodyScrollable) {
            return bodyRef;
        }
        // Otherwise, make the viewport the intersection root
        return undefined;
    }, [bodyRef]);
    const intersectionRef = (0, index_js_1.useIntersection)(onIntersect, {
        rootMargin: `${intersectionMargin}px`,
        root: intersectionRoot,
    });
    const userRowProps = (_a = rowProps === null || rowProps === void 0 ? void 0 : rowProps(row)) !== null && _a !== void 0 ? _a : {};
    const { status, isLoading, ...restUserRowProps } = userRowProps;
    const mergedProps = {
        ...row.getRowProps({ style: { flex: `0 0 auto`, minWidth: '100%' } }),
        ...restUserRowProps,
        ...{
            className: (0, classnames_1.default)('iui-table-row', {
                'iui-table-row-expanded': row.isExpanded && subComponent,
                'iui-loading': isLoading,
            }, userRowProps === null || userRowProps === void 0 ? void 0 : userRowProps.className),
            'aria-selected': row.isSelected || undefined,
            'aria-disabled': isDisabled || undefined,
            'data-iui-status': status,
        },
    };
    const refs = (0, index_js_1.useMergedRefs)(intersectionRef, mergedProps.ref, tableRowRef);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ...mergedProps, ref: refs, onClick: (event) => {
                var _a;
                (_a = mergedProps === null || mergedProps === void 0 ? void 0 : mergedProps.onClick) === null || _a === void 0 ? void 0 : _a.call(mergedProps, event);
                onClick === null || onClick === void 0 ? void 0 : onClick(event, row);
            } }, row.cells.map((cell, index) => {
            return (React.createElement(TableCell_js_1.TableCell, { key: cell.getCellProps().key, cell: cell, cellIndex: index, isDisabled: isDisabled, tableHasSubRows: tableHasSubRows, tableInstance: tableInstance, expanderCell: expanderCell, density: density }));
        })),
        subComponent && (React.createElement(index_js_1.WithCSSTransition, { in: row.isExpanded },
            React.createElement("div", { className: (0, classnames_1.default)('iui-table-row', 'iui-table-expanded-content'), "aria-disabled": isDisabled }, subComponent(row))))));
};
exports.TableRow = TableRow;
const hasAnySelectedSubRow = (row, selectedRowIds) => {
    if (selectedRowIds === null || selectedRowIds === void 0 ? void 0 : selectedRowIds[row.id]) {
        return true;
    }
    return row.subRows.some((subRow) => hasAnySelectedSubRow(subRow, selectedRowIds));
};
exports.TableRowMemoized = React.memo(exports.TableRow, (prevProp, nextProp) => {
    var _a, _b, _c, _d, _e, _f, _g;
    return prevProp.isLast === nextProp.isLast &&
        ((_a = prevProp.state.hiddenColumns) === null || _a === void 0 ? void 0 : _a.length) ===
            ((_b = nextProp.state.hiddenColumns) === null || _b === void 0 ? void 0 : _b.length) &&
        !!((_c = prevProp.state.hiddenColumns) === null || _c === void 0 ? void 0 : _c.every((column, index) => { var _a; return ((_a = nextProp.state.hiddenColumns) === null || _a === void 0 ? void 0 : _a[index]) === column; })) &&
        prevProp.onRowInViewport === nextProp.onRowInViewport &&
        prevProp.onBottomReached === nextProp.onBottomReached &&
        prevProp.onClick === nextProp.onClick &&
        prevProp.row.original === nextProp.row.original &&
        ((_d = prevProp.state.selectedRowIds) === null || _d === void 0 ? void 0 : _d[prevProp.row.id]) ===
            ((_e = nextProp.state.selectedRowIds) === null || _e === void 0 ? void 0 : _e[nextProp.row.id]) &&
        // Check if sub-rows selection has changed and whether to show indeterminate state or not
        prevProp.row.subRows.some((subRow) => hasAnySelectedSubRow(subRow, prevProp.state.selectedRowIds)) ===
            nextProp.row.subRows.some((subRow) => hasAnySelectedSubRow(subRow, nextProp.state.selectedRowIds)) &&
        ((_f = prevProp.state.expanded) === null || _f === void 0 ? void 0 : _f[prevProp.row.id]) ===
            ((_g = nextProp.state.expanded) === null || _g === void 0 ? void 0 : _g[nextProp.row.id]) &&
        prevProp.subComponent === nextProp.subComponent &&
        prevProp.row.cells.every((cell, index) => nextProp.row.cells[index].column === cell.column) &&
        prevProp.isDisabled === nextProp.isDisabled &&
        prevProp.rowProps === nextProp.rowProps &&
        prevProp.expanderCell === nextProp.expanderCell &&
        prevProp.tableHasSubRows === nextProp.tableHasSubRows &&
        prevProp.bodyRef === nextProp.bodyRef &&
        prevProp.state.columnOrder === nextProp.state.columnOrder &&
        !nextProp.state.columnResizing.isResizingColumn &&
        prevProp.state.isTableResizing === nextProp.state.isTableResizing &&
        prevProp.state.sticky.isScrolledToLeft ===
            nextProp.state.sticky.isScrolledToLeft &&
        prevProp.state.sticky.isScrolledToRight ===
            nextProp.state.sticky.isScrolledToRight &&
        prevProp.density === nextProp.density;
});
